import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check, AlertTriangle, FileText, ShieldCheck, Thermometer, ClipboardCheck, Clock } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd, KeywordCrossLinks } from '../../components/MarketingLayout'

const FAQS = [
  {
    q: 'Milyen papírokat kérhet a NÉBIH egy karácsonyi vásári standon?',
    a: 'A leggyakrabban ellenőrzött dokumentumok: minden árult termékhez gyártmánylap (receptúra, összetevők, allergének, eltarthatóság, tárolás), kiírt allergénlista a pultnál, HACCP-alapú önellenőrzési rendszer és a hozzá tartozó napló (hűtési hőmérséklet, tisztítás, átvétel), valamint a felhasznált alapanyagok beszállítói számlái / nyomonkövetési adatai. Alkalmi rendezvényen való árusításhoz jellemzően a helyi járási hivatal élelmiszerlánc-biztonsági osztályához vagy a NÉBIH-hez tett bejelentés is szükséges — ezt a vásár szervezője is gyakran előírja.',
  },
  {
    q: 'Bejglit, mézeskalácsot, szaloncukrot otthon készítek — nekem is kell gyártmánylap?',
    a: 'Ha nem magánfőzésről, hanem értékesítésről van szó (vásár, piac, adventi stand), akkor igen. A saját készítésű élelmiszer minden terméktípusához külön gyártmánylap tartozik, függetlenül attól, hogy cukrászüzemben vagy engedélyezett otthoni konyhában (kistermelői / vendéglátó besorolás) készült. A CukrászDoc-ban a recept beírása után ez 1-2 perc alatt elkészül, nyomtatható PDF-ként.',
  },
  {
    q: 'Mikorra legyen kész a dokumentáció, ha novemberi-decemberi vásárra készülök?',
    a: 'A legtöbb adventi vásár már november közepén nyit, és a szervezők a jelentkezéskor (jellemzően szeptember-október) bekérik a dokumentumokat. Reális, ha a gyártmánylapokat és a HACCP-naplót október végéig összeállítod — így marad időd a hiánypótlásra, ha a szervező vagy a hatóság kiegészítést kér.',
  },
  {
    q: 'Több tucat terméket árulok. Nem lesz ez napokig tartó papírmunka?',
    a: 'A CukrászDoc pont ezt gyorsítja fel: az alapanyagokat egyszer rögzíted (400+ már beépítve), utána minden recepthez egy kattintással áll elő a gyártmánylap, az allergéntáblázat és az etikett-címke. Ami kézzel, Excelben egy hét, az itt jellemzően egy délután.',
  },
  {
    q: 'A HACCP-naplót is tudja a rendszer?',
    a: 'Igen. A napi HACCP-napló (hűtő/fagyasztó hőmérséklet, átvétel, tisztítás, személyi higiénia) a CukrászDoc-ban vezethető és nyomtatható, a vásár időszakára is — így az ellenőrzéskor nincs hiányzó kockás füzet.',
  },
]

const CHECKLIST = [
  { icon: FileText, title: 'Gyártmánylap minden termékre', desc: 'Bejgli, mézeskalács, szaloncukor, forralt bor, kürtőskalács — mindegyikhez külön lap: receptúra, összetevők, allergének, eltarthatóság, tárolás.' },
  { icon: ShieldCheck, title: 'Kiírt allergénlista a pultnál', desc: 'A 14 EU-allergén (1169/2011) írásbeli feltüntetése a nem előrecsomagolt termékeknél is kötelező.' },
  { icon: ClipboardCheck, title: 'HACCP önellenőrzési rendszer + napló', desc: 'Veszélyelemzés, CCP-k, és a vezetett napló: hőmérséklet, tisztítás, alapanyag-átvétel.' },
  { icon: Thermometer, title: 'Hűtési / tárolási hőmérséklet-napló', desc: 'A romlandó töltelékes és tejszínes termékeknél a hidegen tartás dokumentálása.' },
  { icon: FileText, title: 'Beszállítói számlák / nyomonkövetés', desc: 'Az alapanyagok eredetének igazolása — melyik tételből mi készült.' },
  { icon: Check, title: 'Alkalmi árusítás bejelentése', desc: 'A járási hivatal élelmiszerlánc-biztonsági osztályához / NÉBIH-hez; a vásár szervezője is bekéri.' },
]

export default function KaracsonyiVasarPage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Karácsonyi vásárba mész árulni? Ezt kéri a NÉBIH — CukrászDoc</title>
        <meta name="description" content="Adventi vásári élelmiszer-árusítás: gyártmánylap, allergénlista, HACCP napló és nyomonkövetés minden termékre. Állítsd össze a teljes dokumentációt egy délután alatt — próbáld ki ingyen." />
        <link rel="canonical" href="https://cukraszdoc.hu/karacsonyi-vasar-elelmiszer-arusitas" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(FAQS))}</script>
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            <AlertTriangle size={12} />
            Az adventi vásárok november közepén nyitnak — a szervezők már ősszel bekérik a papírokat
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight text-white mb-5">
            Karácsonyi vásárba mész árulni? <span style={{ color: TEAL }}>Ezt kéri a NÉBIH.</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Bejgli, mézeskalács, szaloncukor, kürtőskalács, forralt bor — minden árult termékhez kell gyártmánylap, kiírt allergénlista és HACCP-napló. A CukrászDoc-kal a teljes dokumentációt egy délután alatt összeállítod, kézi Excel-táblázgatás nélkül.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all" style={{ background: TEAL, color: '#fff' }}>
              Állítsd össze a vásári dokumentációd ingyen <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6" style={{ color: DARK }}>Mit ellenőriznek egy adventi vásári standon?</h2>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            A karácsonyi vásárok idején az élelmiszerlánc-felügyelet célzottan ellenőrzi az alkalmi árusokat. A leggyakoribb hiányosság nem a higiénia, hanem a <strong>hiányzó vagy nem naprakész dokumentáció</strong>: nincs gyártmánylap minden termékre, nincs kiírva az allergénlista, üres a HACCP-napló.
          </p>
          <p className="text-base leading-relaxed text-gray-600">
            Egy hiánypótlási felszólítás a szezon közepén a legrosszabbkor jön. A megoldás egyszerű: a papírmunkát még ősszel, egyben letudni — nem terméktípusonként, kézzel újraszámolgatva.
          </p>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: DARK }}>Vásári árusítás — dokumentációs checklist</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CHECKLIST.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <Icon size={22} style={{ color: TEAL }} className="mb-3" />
                <h3 className="font-semibold mb-2 text-base" style={{ color: DARK }}>{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-8 max-w-3xl">
            Tájékoztató jellegű összefoglaló. A pontos követelményekben mindig a helyi járási hivatal élelmiszerlánc-biztonsági osztálya és a vásár szervezőjének kiírása az irányadó.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4" style={{ color: DARK }}>A teljes dokumentáció 3 lépésben</h2>
          <div className="flex flex-col gap-4 mt-10">
            {[
              'Rögzíted a vásárra vitt termékek receptjeit (az alapanyagok nagy része már beépítve).',
              'A rendszer minden termékhez legenerálja a gyártmánylapot, az allergéntáblázatot és az etikett-címkét.',
              'Kinyomtatod a lapokat és a HACCP-naplót — ellenőrzéskor minden egy mappában, naprakészen.',
            ].map((step, i) => (
              <div key={step} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm" style={{ background: DARK, color: TEAL }}>{i + 1}</div>
                <p className="text-base text-gray-600 pt-1">{step}</p>
              </div>
            ))}
          </div>
          <p className="text-base leading-relaxed text-gray-600 mt-8">
            Ha most indulsz vállalkozásként, a teljes lista itt: <Link to="/blog/cukraszda-nyitas-dokumentacio" style={{ color: TEAL, fontWeight: 600 }}>cukrászda nyitás dokumentációja — checklist</Link>.
          </p>
        </div>
      </section>

      <section style={{ background: DARK }} className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <Clock size={28} style={{ color: TEAL }} className="shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold text-white mb-3">Ne az utolsó héten állj neki</h2>
              <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
                A szervezők a jelentkezéskor — jellemzően szeptember-októberben — bekérik a dokumentumokat, és a hiánypótlásra is kell pár nap. Ha most összeállítod, decemberben már csak árulnod kell.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: DARK }}>Gyakori kérdések a vásári árusításról</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <KeywordCrossLinks current="/karacsonyi-vasar-elelmiszer-arusitas" />

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Próbáld ki — az első gyártmánylapod ingyen elkészül</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Regisztráció kártyaszám nélkül. 3 recept erejéig teljesen ingyenes — pont elég, hogy lásd, mennyi időt spórolsz a vásár előtt.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            <Check size={17} /> Ingyenes regisztráció
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
